import { debounce } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import {
  Column,
  createTextColumn,
  DataSheetGridRef,
  DynamicDataSheetGrid,
  keyColumn,
  textColumn,
} from 'react-datasheet-grid';
import { Controller } from 'react-hook-form';

import { Box } from 'ui/Layout';
import {
  sankeyFormFlowsArrayToMap,
  sankeyFormFlowsMapToArray,
} from 'utils/sankey';
import { MAX_TEXT_VALUE_LENGTH } from 'utils/validation';

import { DELAYED_INPUT_DEBOUNCE_MS } from '../../constants';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { newFlowId } from '../../diagram/flows';
import { hasPressedUndoOrRedo } from '../../utils/keyboard';
import { safeParseFloat, toDecimalString } from '../../utils/number';
import { useTeamContext } from '../../utils/team';
import { updateNodesFromFlows } from '../SankeyChart/utils';
import './react-datasheet-grid.css';

const amountColumn = createTextColumn({
  parseUserInput: safeParseFloat,
  parsePastedValue: safeParseFloat,
  formatBlurredInput: toDecimalString,
  formatInputOnFocus: toDecimalString,
});

const DataGrid: React.FC = () => {
  const ref = useRef<DataSheetGridRef>(null);
  const { form, isFetching } = useSankeyFormContext();
  const { teamSettings } = useTeamContext();

  // NOTE: For some reason we need to watch the flows field explicitly to trigger an update of the table when edited from the table.
  form.watch('flows');

  // For dynamic columns it is recommended to use useMemo and DynamicDataSheetGrid, see https://www.npmjs.com/package/react-datasheet-grid/v/2.0.1
  // Trigger an update when tableColumns changes: https://stackoverflow.com/questions/60435481/react-usememo-does-not-update-the-data
  const columns = useMemo(() => {
    let tableColumns: Partial<Column<SankeyFormFlow, any, any>>[] = [
      {
        // @ts-expect-error Should add `null` to the type and polish helpers
        ...keyColumn('source', textColumn),
        title: 'From',
        grow: 1.1,
        minWidth: 50,
      },
      {
        // @ts-expect-error Should add `null` to the type and polish helpers
        ...keyColumn('target', textColumn),
        title: 'To',
        grow: 1.1,
        minWidth: 50,
      },
      {
        ...keyColumn('amount', amountColumn),
        title: 'Amount, current',
        grow: 1,
        minWidth: 50,
      },
      {
        ...keyColumn('amountComparison', amountColumn),
        title: 'Amount, comparison',
        grow: 1,
        minWidth: 50,
      },
    ];
    return tableColumns;
  }, []);

  const debouncedUpdateNodesFromFlows = useMemo(
    () =>
      debounce(
        () => updateNodesFromFlows(form, teamSettings),
        DELAYED_INPUT_DEBOUNCE_MS
      ),
    [form, teamSettings]
  );

  const handleChange = useMemo(
    () => (dataGrid: Partial<SankeyFormFlow>[]) => {
      // Instantly update flow data for responsive table editing
      form.setValue(
        'flows',
        sankeyFormFlowsArrayToMap(
          // Fill in the missing table fields with the default flow values
          dataGrid.map((row, dataGridRowIndex) => ({
            id: newFlowId(dataGridRowIndex),
            // TODO: Cap the node names to a maximum length in the cell editor instead
            source: (row.source ?? '').slice(0, MAX_TEXT_VALUE_LENGTH),
            target: (row.target ?? '').slice(0, MAX_TEXT_VALUE_LENGTH),
            amount: row.amount ?? NaN,
            amountComparison: row.amountComparison ?? NaN,
            dataGridRowIndex,
          }))
        ),
        { shouldDirty: true }
      );
      // Debounce the update of the nodes
      debouncedUpdateNodesFromFlows();
    },
    [debouncedUpdateNodesFromFlows]
  );

  // Capture undo and redo keyboard shortcuts when editing a cell and prevent the default event
  // not to interfere with the global undo and redo shortcuts. Instead, we remove the cell focus
  // and let the global undo and redo actions update the table.
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (hasPressedUndoOrRedo(event)) {
      event.preventDefault();
      ref.current?.setSelection(null);
    }
  }, []);

  return (
    <Box onKeyDown={handleKeyDown}>
      <Controller
        name="flows"
        control={form.control}
        render={({ field }) => (
          <DynamicDataSheetGrid
            value={sankeyFormFlowsMapToArray(field.value)}
            onChange={isFetching ? undefined : handleChange}
            columns={columns}
            ref={ref}
            autoAddRow
            height={500}
            gutterColumn={false}
            style={{ fontSize: '12px' }}
          />
        )}
      />
    </Box>
  );
};

export default DataGrid;
